"use client";

/**
 * Navbar de la landing — logo Qualidot + navegación por anclas + acceso.
 * Usa los tokens de la paleta (claro/oscuro); transparente arriba, se opaca
 * con blur al hacer scroll. Incluye ThemeToggle.
 */

import Link from "next/link";
import { useEffect, useState } from "react";
import { ArrowRight, Menu, X } from "lucide-react";
import { QualidotLogo } from "@/components/QualidotLogo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SITE } from "./_data";

// Las anclas se resuelven contra `basePath`: en la home dan "/#casos"; en una
// página que monta las mismas secciones (ej. /test_pagina) dan
// "/test_pagina#casos" en vez de mandar al usuario de vuelta a la home.
const NAV: { label: string; hash?: string; href?: string }[] = [
  { label: "Casos de uso", hash: "#casos" },
  { label: "Cómo funciona", hash: "#como-funciona" },
  { label: "Integraciones", hash: "#integraciones" },
  { label: "Planes", hash: "#planes" },
  { label: "Preguntas", hash: "#preguntas" },
  { label: "Rubripedia", href: "/rubripedia" },
  { label: "Blog", href: "/blog" },
];

export function Navbar({ solid = false, basePath = "/" }: {
  /** true = siempre opaca con tokens del tema (páginas sin hero oscuro, ej. /blog). */
  solid?: boolean;
  /** Ruta de la página que contiene las secciones ancladas (default: la home). */
  basePath?: string;
}) {
  const [scrolledState, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const scrolled = solid || scrolledState;
  const nav = NAV.map((n) => ({ label: n.label, href: n.href ?? `${basePath}${n.hash}` }));

  useEffect(() => {
    if (solid) return;
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [solid]);

  // El hero ahora es tematizado (aurora sutil sobre bg del tema): tokens siempre.
  const brand = "text-foreground";
  const link = "text-muted-foreground hover:text-foreground";
  const logoBg = "bg-foreground";

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled ? "border-b border-border bg-background/85 backdrop-blur" : "border-b border-transparent bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-2.5 sm:px-6">
        <Link href="/" className="flex items-center gap-2" aria-label="Qualidot — inicio">
          <QualidotLogo className={`${logoBg} h-6 w-6`} />
          <span className={`text-[15px] font-bold tracking-tight ${brand}`}>Qualidot</span>
        </Link>

        <div className="hidden items-center gap-6 md:flex">
          {nav.map((n) => (
            <a key={n.href} href={n.href} className={`text-[13px] font-medium transition-colors ${link}`}>
              {n.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <ThemeToggle />
          <Link
            href={SITE.loginPath}
            className="hidden items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-[13px] font-semibold text-primary-foreground transition-transform hover:scale-[1.03] sm:inline-flex"
          >
            Entrar <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className={`inline-flex h-8 w-8 items-center justify-center rounded-lg md:hidden ${brand}`}
            aria-label="Menú"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      {/* Menú móvil */}
      {open && (
        <div className="border-t border-border bg-background/95 px-4 py-3 md:hidden">
          <div className="flex flex-col gap-1">
            {nav.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-2 text-[13px] font-medium text-muted-foreground hover:bg-muted"
              >
                {n.label}
              </a>
            ))}
            <Link
              href={SITE.loginPath}
              className="mt-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground"
            >
              Entrar a la plataforma <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
